// Today: one row per appointment (time, photo, name, visit type, room, status chip), one open at a time. The open
// row shows its brief, flush with the photo, and the action at the top right of a header row closed by a hairline
// (Antonin, 15 Oct: "should not float there in limbo"). Everything on a row comes from the appointment list
// (toWorklistCard); a wait since check-in is not shown: the wire carries no check-in time.
import { useState } from 'react';
import { Avatar, Button, BrandLoading, EmptyState, Icon, Skeleton } from '@scripsteam/scrips-signal-ds';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { TodayStatusChip } from '@/scheduling/components/today-status-chip';
import { HomeCard } from './home-card';
import type { ReadyRow } from './ready-list-data';
import { Stethoscope } from 'lucide-react';

const initialOfGender = (g: string | undefined): string | undefined => {
  const t = g?.trim().toLowerCase();
  return t === 'female' ? 'F' : t === 'male' ? 'M' : undefined;
};

export interface TodayCardProps {
  rows: readonly ReadyRow[];
  expandedId: string | null;
  onToggle: (id: string) => void;
  onOpenEncounter: (row: ReadyRow) => void;
  /** The patient's details page ("Open patient", once the visit is done). */
  onOpenPatient: (row: ReadyRow) => void;
  /** The "Calendar ↗" link beside the title. */
  onOpenCalendar: () => void;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

interface BriefLine {
  label: string;
  text: string;
}

/** What the wire says about the visit, in the order a clinician reads it. */
function briefLines(r: ReadyRow): BriefLine[] {
  const c = r.card;
  const minutes =
    r.item.minutesDuration ?? (r.startMin != null && r.endMin != null ? r.endMin - r.startMin : undefined);
  const lines: Array<BriefLine | null> = [
    c.comment ? { label: 'Note', text: c.comment } : null,
    r.item.location?.name ? { label: 'Where', text: [r.item.location.name, c.room].filter(Boolean).join(' · ') } : null,
    minutes ? { label: 'Length', text: `${minutes} min` } : null,
    c.language ? { label: 'Language', text: c.interpreterRequired ? `${c.language} · interpreter needed` : c.language } : null,
    c.mrn ? { label: 'MRN', text: c.mrn } : null,
  ];
  return lines.filter((l): l is BriefLine => l != null);
}

/** The patient the visit is for (the wire's own id). */
export const patientIdOf = (r: ReadyRow): string | undefined => r.item.participant?.find((p) => p.patient)?.patient?.id;

/**
 * The row's one action (Antonin, 15 Oct): Open encounter, the one primary, for a patient who has arrived or is in the
 * room; "Open patient" once the visit is done (the patient's details); "Prepare visit" before it (the encounter's pre-visit:
 * `/encounter/:id` lands on the pre-encounter rail until the visit is started, so it is the same route as Open encounter).
 */
export function rowAction(r: ReadyRow): 'encounter' | 'open-patient' | 'prepare' {
  if (r.bucket === 'completed') return 'open-patient';
  if (r.bucket === 'in-progress') return 'encounter';
  return r.glyph === 'half' || r.glyph === 'full' ? 'encounter' : 'prepare';
}

export function TodayCard({ rows, expandedId, onToggle, onOpenEncounter, onOpenPatient, onOpenCalendar, loading, error, onRetry }: TodayCardProps) {
  const calendar = (
    <button type="button" className="home-link" data-open-calendar onClick={onOpenCalendar}>
      Calendar ↗
    </button>
  );
  const [skeleton] = useState(() => isEncounterFlagOn('homeSkeleton'));
  return (
    <HomeCard title="Today" right={calendar} flush data-testid="today">
      {loading ? (
        // `homeSkeleton` ON (Samer, 3 Nov call: "remove the spinner"): the catalog's card loading state, three skeleton lines 16
        // apart, the last at 60% (TitledCard `state="loading"` / SkeletonLines, feat/signal 5c52a404). Words for assistive tech.
        // `?x=-home-skeleton`: the brand spinner, as before.
        <div className="home-state">
          {skeleton ? (
            <div role="status" aria-busy="true" data-slot="today-skeleton" className="flex flex-col gap-4">
              <span className="sr-only">Loading today's appointments</span>
              <Skeleton aria-hidden="true" className="h-4 w-full" />
              <Skeleton aria-hidden="true" className="h-4 w-full" />
              <Skeleton aria-hidden="true" className="h-4 w-3/5" />
            </div>
          ) : (
            <BrandLoading label="Loading today's appointments" />
          )}
        </div>
      ) : error ? (
        <div className="home-state">
          <EmptyState
            icon="warning"
            title="Couldn't load appointments"
            description="Something went wrong fetching today's schedule. Please retry."
            ctaLabel="Retry"
            onCtaClick={onRetry}
          />
        </div>
      ) : rows.length === 0 ? (
        <p className="home-empty home-empty-padded">No appointments today.</p>
      ) : (
        <div className="ready-rows">
          {rows.map((r) => {
            const open = expandedId === r.id;
            const c = r.card;
            const sex = initialOfGender(c.gender);
            const lines = open ? briefLines(r) : [];
            return (
              <div key={r.id} data-schedule-row={r.id} data-expanded={open ? '' : undefined} data-done={r.bucket === 'completed' ? '' : undefined}>
                <button
                  type="button"
                  className="ready-row"
                  aria-expanded={open}
                  aria-controls={`ready-brief-${r.id}`}
                  onClick={() => onToggle(r.id)}
                >
                  <span className="ready-row-time">{r.timeLabel}</span>
                  <Avatar src={c.photoUrl ?? undefined} name={c.patientName} size="sm" tone="patient" />
                  <span className="ready-row-who">
                    <span className="ready-row-name">
                      {c.patientName}
                      {(c.age || sex) && <span className="ready-row-demo"> · {[c.age, sex].filter(Boolean).join(' ')}</span>}
                    </span>
                    {r.visitType && <span className="ready-row-type">{r.visitType}</span>}
                  </span>
                  <span className="ready-row-room">{c.room ?? ''}</span>
                  <span className="ready-row-status" data-schedule-status={c.status}>
                    <TodayStatusChip kind={r.glyph}>{c.status}</TodayStatusChip>
                  </span>
                  {/* The design's chevron (AppointmentRow `.chevron`): the DS `down` glyph, 16, in a box that carries the secondary
                      ink (the glyph draws in currentColor) and turns when the row is open. */}
                  <span className="ready-row-chevron" data-row-chevron aria-hidden="true">
                    <Icon name="down" size={16} />
                  </span>
                </button>
                {open && (
                  <div id={`ready-brief-${r.id}`} data-brief className="ready-brief">
                    <div className="ready-brief-head">
                      <p className="ready-brief-reason">{c.reasonForVisit ?? r.visitType ?? 'Visit'}</p>
                      {rowAction(r) === 'encounter' ? (
                        <Button variant="primary" size="sm" data-open-encounter icon={<Stethoscope className="size-4" aria-hidden />} onClick={() => onOpenEncounter(r)}>
                          Open encounter
                        </Button>
                      ) : (
                        patientIdOf(r) && (
                          <Button
                            variant="secondary"
                            size="sm"
                            data-open-patient={rowAction(r) === 'open-patient' ? '' : undefined}
                            data-prepare-visit={rowAction(r) === 'prepare' ? '' : undefined}
                            onClick={() => (rowAction(r) === 'prepare' ? onOpenEncounter(r) : onOpenPatient(r))}
                          >
                            {rowAction(r) === 'open-patient' ? 'Open patient' : 'Prepare visit'}
                          </Button>
                        )
                      )}
                    </div>
                    {lines.length > 0 && (
                      <dl className="ready-brief-list">
                        {lines.map((l) => (
                          <div key={l.label} className="ready-brief-line">
                            <dt>{l.label}</dt>
                            <dd>{l.text}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </HomeCard>
  );
}
